import {
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Challenge,
  Channel,
  CREATE_CHANNEL,
  DELETE_CHANNEL,
  ErrorResponse,
  GET_CHANNEL_BY_ID,
  GET_CHANNEL_CHALLENGES,
  GET_CHANNEL_MEMBERS,
  GET_CHANNELS,
  GET_ME,
  Me,
  PATCH_CHANNEL,
  User,
} from '@frontend/data-access';
import { StatusTag } from '@frontend/shared/ui';
import {
  App,
  Button,
  Divider,
  Form,
  Input,
  List,
  Modal,
  Spin,
  Tabs,
} from 'antd';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import AssignExperts from '../assign-experts/assign-experts';
import AssignReviewers from '../assign-reviewers/assign-reviewers';
import ReviewerList from '../idea-channel-reviewers/ReviewerList';
import styles from './idea-channel-create.module.less';

/* eslint-disable-next-line */
export interface IdeaChannelCreateProps {
  id?: string;
  onFinish: () => void;
  onCancel: () => void;
}

interface FormValues {
  title: string;
}

export function IdeaChannelCreate({
  id,
  onCancel,
  onFinish,
}: IdeaChannelCreateProps) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const { t: t_admin } = useTranslation('admin');
  const [channelId, setChannelId] = useState<string>();
  const [form] = Form.useForm();
  const [showModal, setShowModal] = useState(false);

  const [getData, { data, loading }] = useLazyQuery<{ channel: Channel }>(
    GET_CHANNEL_BY_ID
  );
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const [getDataMembers, { data: dataMembers }] = useLazyQuery<{
    getChannelMembers: {
      user: User;
    }[];
  }>(GET_CHANNEL_MEMBERS);

  const [getChallenges, { data: dataChallenges, loading: challengeLoading }] =
    useLazyQuery<{
      channelChallenges: Challenge[];
    }>(GET_CHANNEL_CHALLENGES);

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <ExclamationCircleOutlined />
        </span>
      ),
    });
  };

  const [deleteChannel, { loading: loadingDelete }] = useMutation<boolean>(
    DELETE_CHANNEL,
    {
      onError: handleError,
      refetchQueries: [
        {
          query: GET_CHANNELS,
        },
      ],
      onCompleted: () => {
        onFinish();
        setShowModal(false);
      },
    }
  );

  const tabItems = useMemo(() => {
    return [
      {
        key: '1',
        label: 'Channel managers',
        children: (
          <AssignReviewers
            channelId={channelId}
            handleSaveChannel={() => handleCreate()}
          />
        ),
      },
      {
        key: '2',
        label: 'Idea reviewers',
        children: <ReviewerList channelId={channelId as string} />,
      },
      {
        key: '3',
        label: 'Experts',
        children: (
          <AssignExperts
            channelId={channelId}
            handleSaveChannel={() => handleCreate()}
          />
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, meData?.me.id, dataMembers?.getChannelMembers]);

  const handleCompleted = (data: { createChannel: Channel }) => {
    notification.success({
      message: channelId
        ? t('channel_successfully_updated')
        : t('channel_added_successfully'),
      icon: (
        <span className="text-green-600">
          <CheckCircleOutlined />
        </span>
      ),
    });

    // Set channel id if it's created
    if (data.createChannel?.id) {
      setChannelId(data.createChannel.id);
    }
  };

  const [createChannel, { loading: createLoading }] = useMutation<{
    createChannel: Channel;
  }>(CREATE_CHANNEL, {
    onCompleted: handleCompleted,
    refetchQueries: [
      {
        query: GET_CHANNELS,
      },
    ],
  });
  const [updateChannel, { loading: patchLoading }] = useMutation(
    PATCH_CHANNEL,
    { onCompleted: handleCompleted }
  );

  const handleCreate = async (): Promise<Channel | null> => {
    await form.validateFields();
    const values = form.getFieldsValue();
    if (values.title) {
      const { data } = await createChannel({
        variables: {
          input: {
            title: values.title,
          },
        },
      });
      setChannelId(data?.createChannel.id);
      return data?.createChannel || null;
    }
    return null;
  };

  const handleOnFinish = (values: FormValues) => {
    if (channelId) {
      updateChannel({
        variables: {
          input: {
            title: values.title,
            id: channelId,
          },
        },
      });
    } else {
      createChannel({
        variables: {
          input: {
            title: values.title,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ title: data?.channel?.title });
    } else {
      form.resetFields();
    }
  }, [data, form]);

  useEffect(() => {
    getDataMembers({
      variables: {
        channelMemberType: 'CHANNEL_REVIEWER',
        channelId: channelId,
      },
    });
  }, [channelId, getDataMembers]);

  useEffect(() => {
    if (channelId) getData({ variables: { id: channelId } });
  }, [getData, channelId]);

  useEffect(() => {
    setChannelId(id);
    getChallenges({
      variables: {
        id: id,
      },
    });
  }, [getChallenges, id]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spin />
      </div>
    );
  }

  return (
    <div className={styles['container']}>
      {meData?.me.role === 'ADMIN' ? (
        <>
          <Form form={form} layout="vertical" onFinish={handleOnFinish}>
            <Form.Item
              name="title"
              label={t('channel_name')}
              rules={[{ required: true, message: t('please_enter_name') }]}
            >
              <Input
                maxLength={50}
                placeholder={t('enter_idea_channel_name')}
              />
            </Form.Item>

            <Form.Item noStyle>
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                loading={createLoading || patchLoading}
                disabled={createLoading || patchLoading}
              >
                {channelId ? t('save') : t('create')}
              </Button>
            </Form.Item>

            <Divider />

            <Tabs items={tabItems} />
          </Form>

          {data && data?.channel?.title !== 'Open Idea' && (
            <>
              <Divider />
              <div className="p-4 border border-solid border-gray-500 rounded-md space-y-2">
                <div className="font-semibold">
                  {t_admin('idea_hub_delete_title')}
                </div>
                <div>{t_admin('idea_hub_delete_description')}</div>

                {dataChallenges &&
                dataChallenges?.channelChallenges?.length > 0 ? (
                  <>
                    <div className="text-gray-700">
                      <WarningOutlined />{' '}
                      {t_admin('idea_hub_delete_description_2')}
                    </div>
                    <List
                      loading={challengeLoading}
                      pagination={{
                        size: 'small',
                        pageSize: 5,
                        showSizeChanger: false,
                      }}
                      dataSource={dataChallenges?.channelChallenges}
                      renderItem={(item, index) => (
                        <List.Item
                          key={item.id}
                          className="flex justify-between !py-2"
                        >
                          <div className="flex items-center">
                            <Link
                              href={`/intrapreneurship/challenge/${item.id}`}
                              target="_blank"
                            >
                              {item.title} {index}
                            </Link>
                          </div>
                          <div className="text-gray-500 text-sm">
                            <StatusTag type="challenge" status={item.state} />
                          </div>
                        </List.Item>
                      )}
                    />
                  </>
                ) : (
                  <Button
                    loading={false}
                    className="flex-shrink-0 text-red-600"
                    data-test="delete-phase-btn"
                    icon={<DeleteOutlined />}
                    shape="round"
                    variant="outlined"
                    color="danger"
                    onClick={() => setShowModal(true)}
                  >
                    {t('delete')}
                  </Button>
                )}
              </div>
            </>
          )}
          <Modal
            open={showModal}
            onCancel={() => setShowModal(false)}
            footer={null}
          >
            <div className="gap-4 flex flex-col items-center">
              <WarningOutlined className="!text-orange-500 text-6xl" />
              <p className="font-semibold">{t('confirm_remove')}</p>
              <p>{t_admin('idea_hub_delete_channel_confirm')}</p>
              <div className="space-x-4">
                <Button
                  loading={loadingDelete}
                  className="flex-shrink-0 "
                  data-test="delete-phase-btn"
                  icon={<DeleteOutlined />}
                  shape="round"
                  onClick={() => setShowModal(false)}
                >
                  {t('cancel')}
                </Button>
                <Button
                  loading={loadingDelete}
                  className="flex-shrink-0"
                  data-test="delete-phase-btn"
                  icon={<DeleteOutlined />}
                  shape="round"
                  color="danger"
                  variant="solid"
                  onClick={() =>
                    data
                      ? deleteChannel({
                          variables: {
                            id: data?.channel?.id,
                          },
                        })
                      : undefined
                  }
                >
                  {t('delete')}
                </Button>
              </div>
            </div>
          </Modal>
        </>
      ) : (
        <div className="">
          <div className="mb-6">
            <div className="text-gray-600 mb-1">{t('channel_name')}</div>
            <div className="text-gray-800 text-sm font-medium">
              {data?.channel?.title || `${t('loading')}...`}
            </div>
          </div>

          <Divider />

          <Tabs items={tabItems} />
        </div>
      )}
    </div>
  );
}

export default IdeaChannelCreate;
